import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getToken, setToken, clearToken } from '../../config/firebase';
import { backendService } from '../../services/api/backendService';

const PostgresUserContext = createContext();

export function PostgresUserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState(null);
  const [publicUsername, setPublicUsername] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionName, setSubscriptionName] = useState(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [subscriptionCancelAtPeriodEnd, setSubscriptionCancelAtPeriodEnd] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState(null);
  const [groupID, setGroupID] = useState(null);
  const [error, setError] = useState(null);
  const [coins, setCoins] = useState(0);

  // Dark mode — centralized app preference with localStorage persistence
  const [darkMode, setDarkModeState] = useState(() => {
    const saved = localStorage.getItem('onikanji-dark-mode');
    if (saved !== null) return JSON.parse(saved);
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  const resetUserData = useCallback(() => {
    setUser(null);
    setUsername(null);
    setPublicUsername(null);
    setIsAdmin(false);
    setRole(null);
    setGroupID(null);
    setCoins(0);
    setSubscriptionStatus(null);
    setSubscriptionName(null);
    setSubscriptionEndDate(null);
    setSubscriptionCancelAtPeriodEnd(null);
  }, []);

  const loadUser = useCallback(async (token) => {
    if (!token) {
      resetUserData();
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profileResponse = await backendService.getUserProfile(token);

      const userData = {
        uid: profileResponse.uid,
        email: profileResponse.email,
        ...profileResponse,
      };

      setUser(userData);
      setUsername(profileResponse.userName);
      setPublicUsername(profileResponse.publicUsername);
      setIsAdmin(profileResponse.isAdmin || false);
      setRole(profileResponse.role || null);
      setGroupID(profileResponse.groupId || null);
      setCoins(profileResponse.coins || 0);

      // Streak days use the learner's local calendar day; tell the backend the
      // browser's timezone once per visit (and again if it changes, e.g. travel).
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const key = `onikanji_tz_reported_${profileResponse.uid}`;
        if (tz && sessionStorage.getItem(key) !== tz) {
          backendService.reportTimeZone(token, tz).then((r) => {
            if (r && r.saved) sessionStorage.setItem(key, tz);
          });
        }
      } catch (e) {
        // Intl or storage unavailable: streaks stay on UTC days, as before.
      }

      // Fetch subscription data
      try {
        const subscriptionResponse = await backendService.getUserSubscription(profileResponse.uid, token);

        if (subscriptionResponse.success && subscriptionResponse.subscription) {
          const subscription = subscriptionResponse.subscription;
          setSubscriptionStatus(subscription.status);
          setSubscriptionName(subscription.product_name || 'Unknown Subscription');
          setSubscriptionEndDate(new Date(subscription.current_period_end).toLocaleDateString());
          setSubscriptionCancelAtPeriodEnd(subscription.cancel_at_period_end);
        }
      } catch {
        // No subscription is normal
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      // Token might be invalid/expired — clear it
      clearToken();
      resetUserData();
      setError('Session expired. Please sign in again.');
    }

    setLoading(false);
  }, [resetUserData]);

  // On mount, check for existing token
  useEffect(() => {
    const token = getToken();
    loadUser(token);
  }, [loadUser]);

  // Called after magic link verification
  const loginWithToken = useCallback(async (token) => {
    setToken(token);
    await loadUser(token);
  }, [loadUser]);

  const logout = useCallback(async () => {
    const token = getToken();
    if (token) {
      try {
        await backendService.logout(token);
      } catch (err) {
        // Local logout still proceeds — but log loudly so a broken server-side
        // session-revocation path doesn't hide for months like the empty-body
        // bug did.
        console.error('Backend logout failed; session may not be revoked server-side:', err);
      }
    }
    clearToken();
    resetUserData();
  }, [resetUserData]);

  // Apply dark class to <html> whenever darkMode changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleDarkMode = useCallback((value) => {
    const newVal = typeof value === 'boolean' ? value : !darkMode;
    setDarkModeState(newVal);
    localStorage.setItem('onikanji-dark-mode', JSON.stringify(newVal));
  }, [darkMode]);

  const updateCoins = (newCoins) => {
    setCoins(newCoins);
    setUser(prev => prev ? { ...prev, coins: newCoins } : null);
  };

  const markOnboardingCompleteLocal = () => {
    setUser(prev => prev ? { ...prev, hasCompletedOnboarding: true } : null);
  };

  // Compatibility shim: provides getIdToken() for existing hooks
  const firebaseUser = user ? {
    uid: user.uid,
    email: user.email,
    getIdToken: async () => getToken(),
  } : null;

  const contextValue = {
    user,
    username,
    publicUsername,
    loading,
    subscriptionStatus,
    subscriptionName,
    subscriptionEndDate,
    subscriptionCancelAtPeriodEnd,
    role,
    isAdmin,
    groupID,
    error,
    firebaseUser, // Backward-compatible shim with getIdToken()
    coins,
    updateCoins,
    markOnboardingCompleteLocal,
    darkMode,
    toggleDarkMode,
    loginWithToken,
    logout,
    getToken,
  };

  return (
    <PostgresUserContext.Provider value={contextValue}>
      {children}
    </PostgresUserContext.Provider>
  );
}

export function usePostgresUser() {
  const context = useContext(PostgresUserContext);
  if (!context) {
    throw new Error('usePostgresUser must be used within a PostgresUserProvider');
  }
  return context;
}
